import React, { useRef, useEffect, useCallback } from "react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

export type AvatarSize   = "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
export type AvatarShape  = "circle" | "square";
export type AvatarColor  = "blue" | "teal" | "purple" | "pink" | "amber" | "gray";
export type AvatarRing   = "accent" | "success" | "danger" | "warning";
export type AvatarStatus = "online" | "offline" | "busy" | "away";

// ─────────────────────────────────────────────
// CVA — VARIANTES
// Tokens semánticos Contasis:
//   --surface-0 / --surface-2
//   --border-muted / --border-default
//   --text-secondary
//   --primary / --success / --error / --warning
//   --border-focus
// ─────────────────────────────────────────────

const avatarVariants = cva(
  [
    // Base (sin efectos interactivos)
    "relative inline-flex items-center justify-center flex-shrink-0",
    "font-sans font-semibold tracking-tight select-none overflow-hidden aspect-square",
    "bg-[var(--surface-2)] border border-[var(--border-muted)] text-[var(--text-secondary)]",
    "transition-[border-color,filter] duration-150 ease-out",
    // Avatares con imagen siempre usan borde gris (sin importar color)
    "[&:has(img)]:border-[var(--border-default)]",
  ],
  {
    variants: {
      // ── Size ──────────────────────────────
      size: {
        xs:    "w-6 h-6 text-[0.55rem]",
        sm:    "w-8 h-8 text-[0.6875rem]",
        md:    "w-10 h-10 text-[0.78rem]",
        lg:    "w-14 h-14 text-[0.95rem]",
        xl:    "w-20 h-20 text-[1.2rem]",
        "2xl": "w-24 h-24 text-[1.6rem]",
      },

      // ── Shape ─────────────────────────────
      shape: {
        circle: "rounded-full",
        square: "rounded-[var(--radius-sm)]",
      },

      // ── Color (solo con initials o icon) ──
      color: {
        blue:   "bg-[var(--primary-light)] text-[var(--primary)] border-[var(--border-secondary)]",
        teal:   "bg-[hsl(162_60%_95%)] text-[hsl(174_76%_28%)] border-[hsl(168_60%_75%)] dark:bg-[hsl(162_50%_10%)] dark:text-[hsl(174_60%_72%)] dark:border-[hsl(168_40%_25%)]",
        purple: "bg-[hsl(258_100%_97%)] text-[hsl(258_76%_52%)] border-[hsl(258_60%_85%)] dark:bg-[hsl(258_50%_12%)] dark:text-[hsl(258_60%_78%)] dark:border-[hsl(258_35%_28%)]",
        pink:   "bg-[hsl(330_100%_97%)] text-[hsl(330_75%_42%)] border-[hsl(330_60%_84%)] dark:bg-[hsl(330_45%_12%)] dark:text-[hsl(330_60%_78%)] dark:border-[hsl(330_35%_28%)]",
        amber:  "bg-[var(--warning-bg)] text-[var(--warning)] border-[var(--warning-border)]",
        gray:   "bg-[var(--surface-2)] text-[var(--text-secondary)] border-[var(--border-default)]",
      },

      // ── Ring ──────────────────────────────
      ring: {
        accent:  "shadow-[0_0_0_2px_var(--surface-0),0_0_0_3.5px_var(--primary)]",
        success: "shadow-[0_0_0_2px_var(--surface-0),0_0_0_3.5px_var(--success)]",
        danger:  "shadow-[0_0_0_2px_var(--surface-0),0_0_0_3.5px_var(--error)]",
        warning: "shadow-[0_0_0_2px_var(--surface-0),0_0_0_3.5px_var(--warning)]",
      },

      // ── Modifiers ─────────────────────────
      isStatic: {
        true:  "cursor-default",
        false: [
          // Efectos hover/active solo cuando NO es estático
          "hover:border-[var(--border-default)] hover:brightness-[0.96]",
          "[&:has(img)]:hover:brightness-100", // Cancela brightness para imágenes
          "active:brightness-[0.91]",
          "[&:has(img)]:active:brightness-[0.94]",
        ],
      },
      disabled: {
        true:  "opacity-35 grayscale pointer-events-none",
        false: "",
      },
      clickable: {
        true:  "cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--surface-0),0_0_0_4px_var(--border-focus)]",
        false: "",
      },
    },
    defaultVariants: {
      size:      "md",
      shape:     "circle",
      isStatic:  false,
      disabled:  false,
      clickable: false,
    },
  }
);

// ── Square border-radius preciso por tamaño ──
// CVA no puede combinar dos variantes en una clase dinámica
const squareRadiusBySize: Record<AvatarSize, string> = {
  xs:    "rounded-[4px]",
  sm:    "rounded-[5px]",
  md:    "rounded-[6px]",
  lg:    "rounded-[9px]",
  xl:    "rounded-[11px]",
  "2xl": "rounded-[14px]",
};

// ── Status badge ──
const statusSizeMap: Record<AvatarSize, string> = {
  xs:    "w-[7px] h-[7px]",
  sm:    "w-[9px] h-[9px]",
  md:    "w-[11px] h-[11px]",
  lg:    "w-[13px] h-[13px]",
  xl:    "w-[16px] h-[16px]",
  "2xl": "w-[20px] h-[20px]",
};

const statusColorMap: Record<AvatarStatus, string> = {
  online:  "bg-[var(--success)]",
  offline: "bg-[var(--border-default)]",
  busy:    "bg-[var(--error)]",
  away:    "bg-[var(--warning)]",
};

// ─────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────

export interface AvatarProps {
  /** Texto de iniciales — máx 2 caracteres. Se ignora si hay src */
  initials?:  string;
  /** URL de imagen */
  src?:       string;
  /** Alt text para accesibilidad */
  alt?:       string;
  /** Ícono SVG como ReactNode — se usa si no hay src ni initials */
  icon?:      React.ReactNode;
  size?:      AvatarSize;
  shape?:     AvatarShape;
  /** Color de fondo — solo aplica con initials o icon */
  color?:     AvatarColor;
  /**
   * Ring de énfasis.
   * Al pasar de undefined → "accent", el componente dispara
   * automáticamente la animación press + ring grow (C).
   * Los demás rings (success, danger, warning) son estados
   * de datos y no se animan — aparecen directo.
   */
  ring?:      AvatarRing;
  /** Badge de presencia en tiempo real */
  status?:    AvatarStatus;
  /**
   * Sin hover ni cursor interactivo.
   * Usar en tablas, listas densas y vistas de detalle
   * donde el avatar es solo informativo.
   */
  static?:    boolean;
  disabled?:  boolean;
  className?: string;
  onClick?:   () => void;
}

// ─────────────────────────────────────────────
// FALLBACK ICON
// ─────────────────────────────────────────────

const UserFallbackIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-[44%] h-[44%]"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="4" />
    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
  </svg>
);

// ─────────────────────────────────────────────
// AVATAR
// ─────────────────────────────────────────────

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      initials,
      src,
      alt       = "",
      icon,
      size      = "md",
      shape     = "circle",
      color,
      ring,
      status,
      static:   isStatic  = false,
      disabled  = false,
      className = "",
      onClick,
    },
    ref
  ) => {
    const isClickable = !!onClick && !isStatic && !disabled;
    const innerRef    = useRef<HTMLDivElement>(null);

    // Merge ref externo con interno
    const setRef = useCallback(
      (node: HTMLDivElement | null) => {
        (innerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
      },
      [ref]
    );

    // ── Animación automática ring "accent" ──────────────────
    // Detecta cuando ring pasa de undefined → "accent" y dispara
    // la animación press + ring grow (C) internamente.
    // Solo para "accent" porque es el único que responde a
    // interacción del usuario (header/dropdown).
    // Los demás (success, danger, warning) son estados de datos.
    //
    // Requiere en tailwind.config.ts:
    // keyframes: {
    //   "av-ring-press-body": {
    //     "0%":   { transform: "scale(1)"    },
    //     "30%":  { transform: "scale(0.92)" },
    //     "100%": { transform: "scale(1)"    },
    //   },
    //   "av-ring-grow": {
    //     "0%":   { boxShadow: "0 0 0 0px var(--surface-0), 0 0 0 0px var(--primary)", opacity: "0" },
    //     "40%":  { boxShadow: "0 0 0 2px var(--surface-0), 0 0 0 5px var(--primary)",   opacity: "1" },
    //     "100%": { boxShadow: "0 0 0 2px var(--surface-0), 0 0 0 3.5px var(--primary)", opacity: "1" },
    //   },
    // },
    // animation: {
    //   "av-ring-press": "av-ring-press-body .32s cubic-bezier(.4,0,.2,1) forwards, av-ring-grow .38s cubic-bezier(.4,0,.2,1) forwards",
    //   "av-pulse":      "av-pulse 2.4s ease-out infinite",
    // },

    const prevRingRef = useRef<AvatarRing | undefined>(undefined);

    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;

      const activated = !prevRingRef.current && ring === "accent";

      if (activated) {
        const handleAnimationEnd = () => el.classList.remove("animate-av-ring-press");
        el.classList.add("animate-av-ring-press");
        el.addEventListener("animationend", handleAnimationEnd);
        return () => el.removeEventListener("animationend", handleAnimationEnd);
      }

      prevRingRef.current = ring;
    }, [ring]);

    // ── Clases ──────────────────────────────────────────────
    const classes = cn(
      avatarVariants({
        size,
        shape,
        color:     color  ?? undefined,
        ring:      ring   ?? undefined,
        isStatic,
        disabled,
        clickable: isClickable,
      }),
      shape === "square" && squareRadiusBySize[size],
      className
    );

    // ── Contenido ────────────────────────────────────────────
    const content = src ? (
      <img src={src} alt={alt} className="w-full h-full object-cover block" />
    ) : icon ? (
      <span className="flex items-center justify-center w-[44%] h-[44%]">{icon}</span>
    ) : initials ? (
      <span className="flex items-center justify-center w-full h-full leading-none">{initials.slice(0, 2).toUpperCase()}</span>
    ) : (
      <UserFallbackIcon />
    );

    // ── Render ───────────────────────────────────────────────
    const avatar = (
      <div
        ref={setRef}
        className={classes}
        onClick={!disabled ? onClick : undefined}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        aria-disabled={disabled || undefined}
        aria-label={alt || initials}
        onKeyDown={
          isClickable
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick?.();
                }
              }
            : undefined
        }
      >
        {content}
      </div>
    );

    if (status) {
      return (
        <div className="relative inline-flex">
          {avatar}
          <span
            className={cn(
              "absolute bottom-0 right-0 rounded-full",
              "border-2 border-[var(--surface-0)]",
              statusSizeMap[size],
              statusColorMap[status]
            )}
            aria-label={status}
          >
            {status === "online" && (
              <span
                className={cn(
                  "absolute inset-[-3px] rounded-full",
                  "border-[1.5px] border-[var(--success)]",
                  "animate-av-pulse"
                )}
              />
            )}
          </span>
        </div>
      );
    }

    return avatar;
  }
);

Avatar.displayName = "Avatar";

// ─────────────────────────────────────────────
// AVATAR GROUP
// ─────────────────────────────────────────────

export interface AvatarGroupProps {
  children:      React.ReactNode;
  /** Número a mostrar en el overflow counter */
  overflow?:     number;
  /** Debe coincidir con el size de los avatars hijos */
  overflowSize?: AvatarSize;
  className?:    string;
}

const overflowSizeMap: Record<AvatarSize, string> = {
  xs:    "w-6 h-6 text-[0.50rem]",
  sm:    "w-8 h-8 text-[0.60rem]",
  md:    "w-10 h-10 text-[0.70rem]",
  lg:    "w-14 h-14 text-[0.82rem]",
  xl:    "w-20 h-20 text-[1rem]",
  "2xl": "w-24 h-24 text-[1.1rem]",
};

export const AvatarGroup: React.FC<AvatarGroupProps> = ({
  children,
  overflow,
  overflowSize = "md",
  className    = "",
}) => (
  <div className={cn("flex items-center [&>*+*]:-ml-2.5", className)}>
    {/*
      outline en lugar de border — no consume espacio interno.
      Imagen e iniciales mantienen el mismo tamaño visual en el grupo.
    */}
    {React.Children.map(children, (child) =>
      React.isValidElement(child)
        ? React.cloneElement(
            child as React.ReactElement<{ className?: string }>,
            {
              className: cn(
                (child as React.ReactElement<{ className?: string }>).props.className,
                "outline outline-2 outline-[var(--surface-0)] [-outline-offset:1px] hover:z-10"
              ),
            }
          )
        : child
    )}

    {overflow !== undefined && overflow > 0 && (
      <div
        className={cn(
          "inline-flex items-center justify-center flex-shrink-0 rounded-full",
          "outline outline-2 outline-[var(--surface-0)] [-outline-offset:1px]",
          "bg-[var(--surface-2)] text-[var(--text-secondary)] font-inter font-semibold",
          overflowSizeMap[overflowSize]
        )}
        aria-label={`${overflow} más`}
      >
        +{overflow}
      </div>
    )}
  </div>
);

AvatarGroup.displayName = "AvatarGroup";

export default Avatar;
